import TooltipComponent from 'components/tooltip';
import { ServiceEvent } from 'services/service-event';
import { deepAssign } from 'utils/object';
import { addListener } from 'utils/service';
import {
	defineRenderComponentOptions, getInstanceAPI,
} from 'utils/vue';
import { type ComponentPublicInstance } from 'vue';
import { type Cons } from 'vue-facing-decorator/dist/component';

export { ServiceEvent } from 'services/service-event';

export function openTooltip<BodyComponent extends Cons>(
	options: ServiceOptions<
		InstanceType<typeof TooltipComponent<BodyComponent>>
	>,
): ServiceOpenReturn<
	InstanceType<typeof TooltipComponent<BodyComponent>>
> {
	type TooltipComponentType = InstanceType<typeof TooltipComponent<BodyComponent>>;
	const defaultOptions: Omit<
		Partial<ServiceOptions<TooltipComponentType>>,
		'body'
	> & { body: Omit<ServiceOptions<TooltipComponentType>['body'], 'component'> } = {
		body: {
			classes: [],
			listeners: {},
			styles: {},
		},
	};
	let instance: ComponentPublicInstance<TooltipComponentType> = {} as ComponentPublicInstance<TooltipComponentType>;
	let fromCloseListener = false;
	const tooltipAPI: ServiceOpenReturn<TooltipComponentType> = {
		api: {} as any,
		close(originalEvent?: Event) {
			const event = new ServiceEvent({
				type: 'close',
			});

			if (!fromCloseListener) {
				instance.onCloseClick(
					event,
					originalEvent,
				);
			} else {
				fromCloseListener = false;
			}

			if (!event.defaultPrevented) {
				tooltipAPI.destroy();
			}
		},
		destroy() {
			instance.$currentInstance.appContext.app.unmount();
			instance.$currentInstance.appContext.app._container?.remove();
		},
		isOpen: false,
	};
	Object.defineProperty(
		tooltipAPI,
		'isOpen',
		{
			enumerable: true,
			get() {
				return instance.$currentInstance.isMounted;
			},
		},
	);

	const finalOptions: typeof options = deepAssign(
		{},
		defaultOptions,
		options,
	);
	let { listeners } = finalOptions;

	const closeListener = () => {
		fromCloseListener = true;
		tooltipAPI.close();
	};
	listeners = addListener(
		'close',
		closeListener,
		listeners,
	);
	finalOptions.listeners = listeners;

	const parent = (
		options.parent
		|| window.App.router
	);
	const dialogsElement = document.getElementById('dialogs');
	const webAppElement = document.getElementById('webapp');

	const renderComponentOptions = defineRenderComponentOptions({
		component: TooltipComponent,
		element: (
			dialogsElement?.parentElement
			|| webAppElement
		),
		insertBefore: (
			dialogsElement?.parentElement
				? dialogsElement
				: undefined
		),
		props: finalOptions,
		listeners,
	});

	if (
		options.body
		&& 'component' in options.body
		&& options.body.component
	) {
		if (!renderComponentOptions.slots) {
			renderComponentOptions.slots = {};
		}

		renderComponentOptions.slots.default = {
			component: options.body.component,
			props: options.body.props,
			listeners: options.body.listeners,
		};
	}

	instance = parent.$renderComponent(renderComponentOptions);
	tooltipAPI.api = getInstanceAPI(instance);

	requestAnimationFrame(() => {
		if (!finalOptions.isModal) {
			const onWindowClick = (event: Event) => {
				const target = event.target as HTMLElement;
				let instanceElement = instance.$el as Element;

				if (
					instanceElement.nodeType !== Node.ELEMENT_NODE
					&& instanceElement.nextElementSibling
				) {
					instanceElement = instanceElement.nextElementSibling;
				}

				if (
					target !== instanceElement
					&& !instanceElement.contains(target)
					&& !event.defaultPrevented
				) {
					if (instance.anchor) {
						let { anchor } = instance;

						if ('$el' in anchor) {
							anchor = anchor.$el as HTMLElement;
						}

						if (
							anchor !== target
							&& !anchor.contains(target)
						) {
							tooltipAPI.close(event);
						}
					} else {
						tooltipAPI.close(event);
					}
				}
			};
			window.document.body.addEventListener(
				'click',
				onWindowClick,
			);
			instance.onUnmount(() => {
				window.document.body.removeEventListener(
					'click',
					onWindowClick,
				);
			});
		}
	});

	return tooltipAPI;
}

const TooltipService = {
	openTooltip,
};

export default TooltipService;
